import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BookOpen, ExternalLink, LogIn, LogOut, Map, Menu, User as UserIcon, X,
} from "lucide-react";

import { api } from "../api/client";
import { authenticated, initKeycloak, login, logout, profile } from "../auth/keycloak";
import type { WhoAmI } from "../types/api";
import { Button } from "./ds";
import { RateLimitBanner } from "./RateLimitBanner";

type BannerState = { message: string; retryAfter: number } | null;

type NavItemProps = {
  to: string;
  icon: typeof Map;
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "header" | "drawer";
};

const NavItem = ({ to, icon: Icon, children, onClick, variant = "header" }: NavItemProps) => (
  <NavLink
    to={to}
    end
    onClick={onClick}
    className={({ isActive }) => {
      if (variant === "drawer") {
        return [
          "inline-flex items-center gap-3 h-11 px-4 text-[12px] font-medium uppercase tracking-[0.04em] rounded-xs transition-colors mef-focus-ring",
          isActive
            ? "bg-white/10 text-white"
            : "text-white/80 hover:text-white hover:bg-white/5",
        ].join(" ");
      }
      return [
        "inline-flex items-center gap-2 h-8 px-3 text-[11px] font-medium uppercase tracking-[0.04em] rounded-xs transition-colors mef-focus-ring",
        isActive
          ? "bg-white/10 text-white"
          : "text-white/70 hover:text-white hover:bg-white/5",
      ].join(" ");
    }}
  >
    <Icon className="h-4 w-4" />
    {children}
  </NavLink>
);

export function Layout() {
  const [authed, setAuthed] = useState(false);
  const [whoami, setWhoami] = useState<WhoAmI | null>(null);
  const [banner, setBanner] = useState<BannerState>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    initKeycloak().then(() => setAuthed(authenticated()));
  }, []);

  useEffect(() => {
    api.whoami().then(setWhoami).catch(() => {});
  }, [authed]);

  // Always collapse the slide-out drawer when the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const prof = profile();
  const planLabel = whoami?.plan ?? "anonymous";
  const rateLabel =
    whoami && whoami.rate_limit_per_min < 0
      ? "unlimited"
      : `${whoami?.rate_limit_per_min ?? "—"}/min`;
  const signedInLabel = prof?.preferred_username ?? prof?.email ?? "Sign out";

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {banner && (
        <RateLimitBanner
          message={banner.message}
          retryAfter={banner.retryAfter}
          onDismiss={() => setBanner(null)}
        />
      )}

      {/* Header — kept as a single h-12 row that adapts; not the reusable
          DS <Header /> because mobile demands a different layout (hamburger
          on the left, brand centred, single utility on the right). */}
      <header className="h-12 shrink-0 flex items-center bg-mef-dark text-white shadow-mef-sm relative z-40 px-3 sm:px-6 gap-2 sm:gap-4">
        {/* Mobile hamburger — only visible below md */}
        <button
          type="button"
          aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileNavOpen((v) => !v)}
          className="md:hidden h-9 w-9 -ml-1 inline-flex items-center justify-center rounded-xs hover:bg-white/10 mef-focus-ring"
        >
          {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <Link to="/" className="flex items-center gap-2 sm:gap-3 mef-focus-ring shrink-0">
          <img
            src="/mef/logos/myeasyfarm-sidebar-logo.png"
            alt="MyEasyFarm"
            className="h-7 w-7 sm:h-8 sm:w-8"
          />
          <div className="leading-tight">
            <div className="text-[13px] font-medium tracking-[0.02em] text-white">
              mef-agroref
            </div>
            <div className="hidden sm:block text-[10px] uppercase tracking-[0.08em] text-white/60">
              Soil &amp; weather reference
            </div>
          </div>
        </Link>

        {/* Desktop nav — hidden on small viewports */}
        <nav className="hidden md:flex flex-1 items-center gap-1 min-w-0">
          <NavItem to="/" icon={Map}>
            Explore
          </NavItem>
          <NavItem to="/docs" icon={BookOpen}>
            Data sources
          </NavItem>
          {authed && (
            <NavItem to="/profile" icon={UserIcon}>
              Profile
            </NavItem>
          )}
        </nav>

        {/* Spacer pushes utility right on mobile (no nav between brand & util). */}
        <div className="md:hidden flex-1" />

        {/* Utility */}
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 h-8 px-3 text-[11px] font-medium uppercase tracking-[0.04em] rounded-xs border-[1.2px] border-mef-green text-white hover:bg-mef-green hover:shadow-mef-button transition-all mef-focus-ring"
          >
            API
            <ExternalLink className="h-3 w-3" />
          </a>
          {authed ? (
            <>
              {/* Profile link icon — visible on mobile only, full nav covers it on desktop */}
              <Link
                to="/profile"
                aria-label="Profile"
                className="md:hidden h-8 w-8 inline-flex items-center justify-center rounded-xs text-white/80 hover:bg-white/10 mef-focus-ring"
              >
                <UserIcon className="h-4 w-4" />
              </Link>
              <Button
                size="sm"
                variant="dark"
                onClick={logout}
                leadingIcon={<LogOut className="h-3.5 w-3.5" />}
                className="hidden sm:inline-flex max-w-[180px] truncate"
              >
                {signedInLabel}
              </Button>
              <button
                type="button"
                aria-label="Sign out"
                onClick={logout}
                className="sm:hidden h-8 w-8 inline-flex items-center justify-center rounded-xs text-white/80 hover:bg-white/10 mef-focus-ring"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Button
              size="sm"
              variant="dark"
              onClick={login}
              leadingIcon={<LogIn className="h-3.5 w-3.5" />}
            >
              <span className="hidden xs:inline">Sign in</span>
              <span className="xs:hidden">Sign in</span>
            </Button>
          )}
        </div>
      </header>

      {/* Mobile slide-out drawer for nav */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
          <nav className="relative w-64 max-w-[80vw] h-full bg-mef-dark text-white pt-3 px-3 flex flex-col gap-1 shadow-mef-popup">
            <div className="px-4 pb-2 mb-1 border-b border-white/10 text-[10px] uppercase tracking-[0.08em] text-white/40">
              Navigation
            </div>
            <NavItem to="/" icon={Map} variant="drawer">
              Explore
            </NavItem>
            <NavItem to="/docs" icon={BookOpen} variant="drawer">
              Data sources
            </NavItem>
            {authed && (
              <NavItem to="/profile" icon={UserIcon} variant="drawer">
                Profile
              </NavItem>
            )}
            <a
              href="/api/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 h-11 px-4 text-[12px] font-medium uppercase tracking-[0.04em] rounded-xs text-white/80 hover:text-white hover:bg-white/5 mef-focus-ring"
            >
              <ExternalLink className="h-4 w-4" />
              API docs
            </a>
            <div className="mt-auto mb-3 px-4 py-3 border-t border-white/10 text-[11px] text-white/60">
              <div className="uppercase tracking-[0.04em] text-white/40 mb-0.5">Plan</div>
              <div className="font-medium text-white/90">{planLabel}</div>
              <div className="mt-2 uppercase tracking-[0.04em] text-white/40 mb-0.5">Rate limit</div>
              <div className="font-medium text-white/90">{rateLabel}</div>
            </div>
          </nav>
        </div>
      )}

      {/* Status strip — collapses to a single line on small screens. */}
      <div className="border-b border-mef-separator bg-mef-subtle px-3 sm:px-6 py-1.5 text-[11px] uppercase tracking-[0.04em] text-black/60 flex flex-wrap gap-x-6 gap-y-1">
        <span>
          Plan: <span className="font-medium text-black/80">{planLabel}</span>
        </span>
        <span>
          Rate limit: <span className="font-medium text-black/80">{rateLabel}</span>
        </span>
      </div>

      <main className="flex flex-1 min-h-0 flex-col">
        <Outlet context={{ setBanner }} />
      </main>

      <footer className="border-t border-mef-separator bg-white px-3 sm:px-6 py-3 sm:py-4 text-[10px] sm:text-[11px] uppercase tracking-[0.04em] text-black/40">
        © {new Date().getFullYear()} MyEasyFarm · mef-agroref
      </footer>
    </div>
  );
}
